// src/tracks/Track.js
// A Track represents a single instrument lane on the timeline.
//
// State:
//   - id, name, instrumentId
//   - notes: array of { time (sec), duration (sec), pitch (MIDI), velocity (0..1) }
//   - mixer: { volume, pan, reverbSend, muted, soloed }
//   - color (from instrument entry)
//
// Audio routing (built in TrackManager when instrument is bound):
//   instrument -> channel -> master
//                          -> sendGain -> reverbSend -> reverb -> master

import * as Tone from 'tone';

let _idCounter = 0;
const nextId = () => `track-${++_idCounter}`;

export class Track {
  constructor({ instrumentId, name = null, color = '#888', mixer = {} } = {}) {
    this.id = nextId();
    this.instrumentId = instrumentId;
    this.name = name;
    this.color = color;
    this.notes = [];
    this.clips = [];
    this.mixer = {
      volume: 0,     // dB
      pan: 0,        // -1..1
      reverbSend: 0.25, // 0..1
      muted: false,
      soloed: false,
      ...mixer
    };

    // Audio nodes (created by TrackManager when instrument is bound)
    this.channel = null;
    this.sendGain = null;
    this.instrument = null;
    this.isReady = false; // true once the instrument is loaded & ready to play

    // Notes that were just added (e.g. via piano recording) are stored
    // here with the time they were marked. The Sequencer reads this set
    // during draw() to flash newly-recorded notes briefly.
    this._recentNoteIds = new Map(); // id -> timestamp (ms)

    // Listeners (UI re-render hooks)
    this._listeners = new Set();
  }

  setName(name) {
    this.name = name;
    this._emit();
  }

  /**
   * Mark a note as "recently added" so the Sequencer can render a
   * short flash effect (bright border) for ~1.5 seconds. The timestamp
   * is wall-clock ms, and the Sequencer's draw loop will silently
   * drop entries older than the flash duration.
   */
  markRecent(noteId) {
    if (!noteId) return;
    this._recentNoteIds.set(noteId, performance.now());
    this._emit();
  }

  onChange(fn) {
    this._listeners.add(fn);
    return () => this._listeners.delete(fn);
  }

  _emit() {
    this._listeners.forEach((fn) => fn(this));
  }

  setMixer(partial) {
    this.mixer = { ...this.mixer, ...partial };
    this._applyMixer();
    this._emit();
  }

  /** Apply current mixer values to the Tone nodes (if bound). */
  _applyMixer() {
    if (this.channel) {
      this.channel.volume.rampTo(this.mixer.muted ? -60 : this.mixer.volume, 0.05);
      this.channel.pan.rampTo(this.mixer.pan, 0.05);
    }
    if (this.sendGain) {
      this.sendGain.gain.rampTo(this.mixer.reverbSend, 0.05);
    }
  }

  addNote(note) {
    // note: { time, duration, pitch, velocity }
    const n = {
      id: `n-${Math.random().toString(36).slice(2, 9)}`,
      time: note.time ?? 0,
      duration: note.duration ?? 0.5,
      pitch: note.pitch ?? 60, // MIDI default
      velocity: note.velocity ?? 0.8
    };
    this.notes.push(n);
    this._emit();
    return n;
  }

  updateNote(id, partial) {
    const n = this.notes.find((x) => x.id === id);
    if (!n) return;
    Object.assign(n, partial);
    this._emit();
  }

  removeNote(id) {
    const idx = this.notes.findIndex((x) => x.id === id);
    if (idx >= 0) {
      this.notes.splice(idx, 1);
      this._emit();
    }
  }

  clearNotes() {
    this.notes = [];
    this._emit();
  }

  /** Add a ClipInstance referencing a global clip definition. */
  addClipInstance(clipId, startTime, repeatCount = 1) {
    const inst = {
      id: `inst-${Math.random().toString(36).slice(2, 9)}`,
      clipId,
      startTime,
      repeatCount: Math.max(1, repeatCount)
    };
    this.clips.push(inst);
    this._emit();
    return inst;
  }

  /** Update a clip instance by id. Partial merge. */
  updateClipInstance(id, partial) {
    const inst = this.clips.find((c) => c.id === id);
    if (!inst) return false;
    Object.assign(inst, partial);
    this._emit();
    return true;
  }

  /** Remove a clip instance by id. */
  removeClipInstance(id) {
    const idx = this.clips.findIndex((c) => c.id === id);
    if (idx < 0) return false;
    this.clips.splice(idx, 1);
    this._emit();
    return true;
  }

  setNotes(notes) {
    this.notes = notes.map((n) => ({
      id: n.id ?? `n-${Math.random().toString(36).slice(2, 9)}`,
      time: n.time,
      duration: n.duration,
      pitch: n.pitch,
      velocity: n.velocity ?? 0.8
    }));
    this._emit();
  }

  dispose() {
    if (this.instrument && this.instrument.dispose) this.instrument.dispose();
    if (this.channel) this.channel.dispose();
    if (this.sendGain) this.sendGain.dispose();
    this.instrument = null;
    this.channel = null;
    this.sendGain = null;
    this.clips = [];
  }
}
